// Funcionalidades (telas) de cada módulo, iguais ao catálogo do backend (SIGAF.tblFuncionalidade, M050 e
// Funcionalidade{Módulo}): o usuário tem as dos perfis do GovHub do seu grupo e o gestor da empresa tem todas.
// A Administração (11) tem as suas em model/adm/adm-enums.ts.

export const ModuleId = {
    Sdp: 1,
    Ser: 2,
    Spm: 3,
    Sic: 4,
    Ouv: 7,
    Ged: 8,
    Scp: 9,
    Sge: 10,
} as const;

export const SdpFunctionality = {
    Submissions: 'sdp.remessas',
    OfficialDiary: 'sdp.diario-oficial',
} as const;

export const SerFunctionality = {
    SendSubmission: 'ser.enviar-remessa',
    Submissions: 'ser.remessas',
} as const;

// Portal Municipal: as telas do front "apm"
export const SpmFunctionality = {
    Parameters: 'spm.parametros',
    Home: 'spm.home',
    Banners: 'spm.banners',
    Menus: 'spm.menus',
    Pages: 'spm.paginas',
    News: 'spm.noticias',
    QuestionCategories: 'spm.perguntas-respostas.categorias',
    Questions: 'spm.perguntas-respostas',
    Biddings: 'spm.licitacoes',
    ManagementUnits: 'spm.unidades-gestoras',
    Contracts: 'spm.contratos',
    Audits: 'spm.auditorias',
    GovernmentProjects: 'spm.projetos-governo',
    Agreements: 'spm.convenios-repasses',
    PublicAccounts: 'spm.contas-publicas',
} as const;

export const SicFunctionality = {
    Requests: 'sic.pedidos',
    Subjects: 'sic.assuntos',
    Departments: 'sic.departamentos',
    Settings: 'sic.configuracoes',
} as const;

export const OuvFunctionality = {
    Manifestations: 'ouv.manifestacoes',
    Subjects: 'ouv.assuntos',
    Departments: 'ouv.departamentos',
    Settings: 'ouv.configuracoes',
} as const;

export const GedFunctionality = {
    Documents: 'ged.documentos',
    Agents: 'ged.agentes',
    Models: 'ged.modelos',
    Sectors: 'ged.setores',
    Folders: 'ged.pastas',
    Subjects: 'ged.assuntos',
    Locations: 'ged.localizacoes',
    Profiles: 'ged.perfis',
    Audit: 'ged.auditoria',
    Trash: 'ged.lixeira',
    Retention: 'ged.temporalidade',
    Processing: 'ged.processamento',
    Reconciliation: 'ged.reconciliacao',
} as const;

export const ScpFunctionality = {
    Units: 'scp.unidades',
    PriceRegistrationIntent: 'scp.intencao-registro-preco',
    Purchases: 'scp.compras-licitacoes',
    Contracts: 'scp.contratos',
} as const;

export const SgeFunctionality = {
    PoliticalParties: 'sge.partidos-politicos',
    Parliamentarians: 'sge.parlamentares',
    Suppliers: 'sge.fornecedores',
    Amendments: 'sge.emendas',
    BudgetUnits: 'sge.unidades-orcamentarias',
    BudgetPrograms: 'sge.programas-orcamentarios',
    BudgetActions: 'sge.acoes-orcamentarias',
    Inbox: 'sge.caixa-de-entrada',
    MonitoredCnpjs: 'sge.cnpjs-monitorados',
    Syncs: 'sge.sincronizacoes',
} as const;

type ValueOf<T> = T[keyof T];

export type ModuleFunctionalityCode =
    | ValueOf<typeof SdpFunctionality>
    | ValueOf<typeof SerFunctionality>
    | ValueOf<typeof SpmFunctionality>
    | ValueOf<typeof SicFunctionality>
    | ValueOf<typeof OuvFunctionality>
    | ValueOf<typeof GedFunctionality>
    | ValueOf<typeof ScpFunctionality>
    | ValueOf<typeof SgeFunctionality>;

// Caminho de cada tela do menu → funcionalidades que a abrem (basta uma). As sub-rotas herdam a do caminho pai
// (/emendas/cadastrar, /documentos/:id…). O Dashboard não está aqui: basta ter alguma funcionalidade do módulo.
export const MODULE_SCREEN_FUNCTIONALITIES: Record<number, Record<string, ModuleFunctionalityCode[]>> = {
    [ModuleId.Sdp]: {
        '/remessas-publicacao': [SdpFunctionality.Submissions],
        '/diario-oficial': [SdpFunctionality.OfficialDiary],
    },
    [ModuleId.Ser]: {
        '/enviar-remessa': [SerFunctionality.SendSubmission],
        '/remessas': [SerFunctionality.Submissions],
    },
    [ModuleId.Spm]: {
        '/parametros': [SpmFunctionality.Parameters],
        '/home': [SpmFunctionality.Home],
        '/banners': [SpmFunctionality.Banners],
        '/menus': [SpmFunctionality.Menus],
        '/paginas': [SpmFunctionality.Pages],
        '/noticias': [SpmFunctionality.News],
        '/perguntas-respostas/categorias': [SpmFunctionality.QuestionCategories],
        '/perguntas-respostas': [SpmFunctionality.Questions],
        '/licitacoes': [SpmFunctionality.Biddings],
        '/unidades-gestoras': [SpmFunctionality.ManagementUnits],
        '/contratos': [SpmFunctionality.Contracts],
        '/auditorias': [SpmFunctionality.Audits],
        '/projetos-governo': [SpmFunctionality.GovernmentProjects],
        '/convenios-repasses': [SpmFunctionality.Agreements],
        '/contas-publicas': [SpmFunctionality.PublicAccounts],
    },
    [ModuleId.Sic]: {
        '/pedidos': [SicFunctionality.Requests],
        '/assuntos': [SicFunctionality.Subjects],
        '/departamentos': [SicFunctionality.Departments],
        '/configuracoes': [SicFunctionality.Settings],
    },
    [ModuleId.Ouv]: {
        '/manifestacoes': [OuvFunctionality.Manifestations],
        '/assuntos': [OuvFunctionality.Subjects],
        '/departamentos': [OuvFunctionality.Departments],
        '/configuracoes': [OuvFunctionality.Settings],
    },
    [ModuleId.Ged]: {
        '/documentos': [GedFunctionality.Documents],
        '/agentes': [GedFunctionality.Agents],
        '/modelos': [GedFunctionality.Models],
        '/setores': [GedFunctionality.Sectors],
        '/pastas': [GedFunctionality.Folders],
        '/assuntos': [GedFunctionality.Subjects],
        '/localizacoes': [GedFunctionality.Locations],
        '/perfis': [GedFunctionality.Profiles],
        '/auditoria': [GedFunctionality.Audit],
        '/lixeira': [GedFunctionality.Trash],
        '/temporalidade': [GedFunctionality.Retention],
        '/processamento': [GedFunctionality.Processing],
        '/reconciliacao': [GedFunctionality.Reconciliation],
    },
    [ModuleId.Scp]: {
        '/unidades-entidade': [ScpFunctionality.Units],
        '/intencao-registro-preco': [ScpFunctionality.PriceRegistrationIntent],
        '/compras-licitacoes': [ScpFunctionality.Purchases],
        '/contratos': [ScpFunctionality.Contracts],
    },
    [ModuleId.Sge]: {
        '/partidos-politicos': [SgeFunctionality.PoliticalParties],
        '/parlamentares': [SgeFunctionality.Parliamentarians],
        '/fornecedores': [SgeFunctionality.Suppliers],
        '/emendas': [SgeFunctionality.Amendments],
        '/unidades-orcamentarias': [SgeFunctionality.BudgetUnits],
        '/programas-orcamentarios': [SgeFunctionality.BudgetPrograms],
        '/acoes-orcamentarias': [SgeFunctionality.BudgetActions],
        '/caixa-de-entrada': [SgeFunctionality.Inbox],
        '/cnpjs-monitorados': [SgeFunctionality.MonitoredCnpjs],
        '/sincronizacoes': [SgeFunctionality.Syncs],
    },
};
